```vue
<template>
    <button
        class="relative flex justify-center items-center"
        :class="[buttonClasses, textClasses]"
        :disabled="disabled || processing"
        @click="debouncedClickHandler"
    >
        <slot />
        <div
            v-if="processing"
            class="absolute flex w-full h-full items-center justify-center"
            :class="[buttonClasses, textClasses]"
        >
            <LoadingIcon class="w-5 h-5 mr-2" />
            {{ $t("generic.ls_generic_loading") }}
        </div>
        <PromoTag v-if="hasPromoTag" />
    </button>
</template>

<script setup>
import LoadingIcon from "@/Components/Icons/LoadingIcon.vue";
import PromoTag from "@/Components/Tags/PromoTag.vue";
import { computed, onBeforeUnmount } from "vue";
import { debounce } from "lodash";

const emit = defineEmits(["click"]);

const props = defineProps({
    size: {
        type: String,
        validator(value) {
            return ["s", "m", "l"].includes(value);
        },
        default: "m",
    },
    color: {
        type: String,
        validator(value) {
            return [
                "red",
                "dark-red",
                "green",
                "lemon-green",
                "white",
                "light-gray",
                "dark-gray",
                "transparent",
                "alert",
                "premium",
                "promo",
            ].includes(value);
        },
        default: "red",
    },
    block: {
        type: Boolean,
        default: false,
    },
    text: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    selected: {
        type: Boolean,
        default: false,
    },
    icon: {
        type: Boolean,
        default: false,
    },
    roundedClass: {
        type: String,
        default: "rounded-lg",
    },
    setup: {
        type: Boolean,
        default: false,
    },
    question: {
        type: Boolean,
        default: false,
    },
    onboarding: {
        type: Boolean,
        default: false,
    },
    textClasses: {
        type: [String, Object],
        default: "font-semibold text-button",
    },
    padding: {
        type: String,
        default: "",
    },
    processing: {
        type: Boolean,
        default: false,
    },
    hasPromoTag: {
        type: Boolean,
        default: false,
    },
    debounceDelay: {
        type: Number,
        default: 200,
    },
});

const buttonClasses = computed(() => {
    const padding = computed(() => {
        if (props.padding != "") {
            return props.padding;
        }

        if (props.icon) {
            return props.size === "s" ? 1.5 : 2.5;
        }

        if (props.size === "s") {
            return "px-4 py-2";
        }

        if (props.size === "m") {
            return "px-6 py-3.5";
        }

        if (props.size === "l") {
            return "px-8 py-4";
        }

        return "px-6 py-3.5";
    });

    const base = [
        props.icon ? `p-${padding.value}` : padding.value,
        props.roundedClass,
        { "text-button-s": props.size === "s" },
        { "text-button": props.size === "m" },
        { "text-button-l": props.size === "l" },
        { "w-full": props.block },
    ];

    if (props.selected) {
        return [
            ...base,
            "shadow-inner rounded-lg",
            {
                "bg-gray-840  hover:shadow-md text-white":
                    props.setup && !props.question,
                "bg-gray-100 text-gray-800": props.question,
                "bg-lemon-green hover:bg-lemon-green-light active:bg-lemon-green-dark text-white":
                    !props.disabled && !props.setup && !props.question,
                "bg-lemon-green-light hover:bg-lemon-green-light active:bg-lemon-green-dark text-white":
                    props.disabled && !props.setup && !props.question,
            },
        ];
    }

    return [
        {
            "shadow-button": props.color !== "transparent",
        },
        {
            "hover:shadow-button-hover active:shadow-button-active":
                !props.disabled && props.color !== "transparent",
            "hover:shadow-button cursor-default":
                props.disabled && props.color !== "transparent",
        },
        {
            "text-white": props.color === "red",
            "bg-blend-multiply bg-gradient-to-b from-button-gradient-from to-button-gradient-to bg-red active:bg-red-dark":
                !props.disabled && props.color === "red",
            "bg-gray-250": props.disabled && props.color === "red",
        },
        {
            "text-white active:bg-red-dark": props.color === "dark-red",
            "bg-blend-multiply bg-red-gradient-shadowy":
                !props.disabled && props.color === "dark-red",
            "bg-red-lighter": props.disabled && props.color === "dark-red",
        },
        {
            "bg-blend-multiply bg-gradient-to-b from-button-gradient-from to-button-gradient-to bg-green-dark text-white":
                !props.disabled && props.color === "green",
        },
        {
            "text-white active:bg-red-600": props.color === "alert",
            "bg-blend-multiply bg-gradient-to-b from-button-gradient-from to-button-gradient-to bg-alert":
                !props.disabled && props.color === "alert",
            "bg-red-300": props.disabled && props.color === "alert",
        },
        {
            "italic shadow-button-active text-gray-700 active:bg-gray-400":
                props.color === "light-gray",
            "bg-gray-100 hover:bg-gray-400":
                props.color === "light-gray" && !props.disabled,
            "bg-gray-100": props.color === "light-gray" && props.disabled,
        },
        {
            "text-white active:bg-gray-900": props.color === "dark-gray",
            "bg-gray-840 hover:bg-gray-900":
                props.color === "dark-gray" && !props.disabled,
            "bg-gray-250": props.color === "dark-gray" && props.disabled,
        },
        {
            "bg-blend-multiply bg-gradient-to-b from-button-gradient-from to-button-gradient-to bg-lemon-green text-white":
                !props.disabled && props.color === "lemon-green",
            "text-gray-600 bg-white":
                props.disabled && props.color === "lemon-green",
        },
        {
            "bg-premium-button-gradient text-white":
                !props.disabled && props.color === "premium",
            "text-gray-600 bg-white":
                props.disabled && props.color === "premium",
        },
        {
            "bg-white active:bg-none active:bg-gray-100 hover:bg-gray-100":
                props.color === "white" && !props.setup,
            "bg-white active:bg-none active:bg-gray-100 hover:bg-white":
                props.color === "white" && props.setup,
            "text-gray-600": props.disabled && props.color === "white",
            "text-gray-800": !props.disabled && props.color === "white",
        },
        {
            "text-white": props.color === "promo",
            "bg-gradient-red-discount active:bg-gradient-red-discount":
                !props.disabled && props.color === "promo",
            "bg-gray-250": props.disabled && props.color === "promo",
        },
        props.text
            ? "underline text-gray-800"
            : [
                  ...base,
                  {
                      "bg-transparent hover:bg-gray-100 active:bg-gray-100 text-gray-550 hover:text-black active:bg-none active:bg-gray-100 shadow-none":
                          props.color === "transparent" && !props.onboarding,
                      "bg-transparent hover:bg-gray-100 active:bg-gray-100 text-gray-800 hover:text-black active:bg-none active:bg-gray-100 shadow-none":
                          props.color === "transparent" && props.onboarding,
                      "text-gray-600":
                          props.disabled && props.color === "transparent",
                      "text-gray-800":
                          !props.disabled && props.color === "white",
                  },
              ],
    ];
});

const debouncedClickHandler = computed(() => {
    const handler = debounce((event) => {
        if (!props.disabled && !props.processing) {
            emit("click", event);
        }
    }, props.debounceDelay);

    onBeforeUnmount(() => {
        handler.cancel();
    });

    return handler;
});
</script>
